import './ProjectsListTable.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowAltCircleUp, faDownload, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button, IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { S_Link } from '../../../../design/links/S_Link';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { ProjectStatusBadge } from '../../../../modules/common/statusesBadges/projectStatus/ProjectStatusBadge';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { ProjectRole } from '../../../models/userRole.model';
import { Home } from '../../../routes/config/Home';
import { useAnnotationPermissions } from '../../annotation/annotation.permissions';
import { PublishButtonContainer } from '../../projectDetails/containers/PublishButton.container';
import { ProjectStatus } from '../projects.model';
import { useProjectPermissions } from '../projects.permissions';
import { IProjectListItem } from '../projects.store';

library.add(faDownload, faArrowAltCircleUp);

export interface IProjectsListTableProps {
  workspaceId: string;
  projects: IProjectListItem[];
  isLoading: boolean;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: S_PaginationProps;
  onDelete: (id: string) => Promise<void>;
  onAnnotate: (id: string) => void;
  onReview: (id: string) => void;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onGoToClarifications?: (id: string) => Promise<void>;
}

export const ProjectsListTable = observer((props: IProjectsListTableProps): ReactElement => {
  const { t } = useTranslator();
  const context = useContext(Settings);
  const projectsPermissions = useProjectPermissions();
  const annotationsPermissions = useAnnotationPermissions();

  const renderName = (projectItem: IProjectListItem): ReactElement => {
    const name = projectItem.name ? projectItem.name : `(${t('untitled')})`;

    return (
      <RowLayout size='xs'>
        {!projectsPermissions.canView().allowed ? (
          <span>{name}</span>
        ) : (
          <S_Link2
            color='text'
            to={
              projectItem.status === ProjectStatus.Published
                ? Home.Projects.Details.Overview.withParams({ projectId: projectItem.id, workspaceId: props.workspaceId })
                : Home.Projects.Details.AnnotationView.withParams({ projectId: projectItem.id, workspaceId: props.workspaceId })
            }
          >
            {name}
          </S_Link2>
        )}
        {projectItem.imageSizeInMB >= 1024 && (
          <FontAwesomeIcon
            title={t('dataset_size_limit_tooltip')}
            className='warning-icon'
            icon={faTriangleExclamation}
          />
        )}
      </RowLayout>
    );
  };

  const renderAwaiting = (projectItem: IProjectListItem): ReactElement => {
    return (
      <>
        {projectItem.awaitingCount}
        {projectItem.myRejectedCount > 0 && <span className='red'> ({projectItem.myRejectedCount})</span>}
      </>
    );
  };

  const renderModifiedDate = (projectItem: IProjectListItem): ReactElement => <>{toLocaleDateTimeString(projectItem.modifiedDate)}</>;

  const renderStatus = (projectItem: IProjectListItem): ReactElement => <ProjectStatusBadge projectStatus={projectItem.status} />;

  const renderUnansweredClarifications = (projectItem: IProjectListItem): ReactElement => {
    const count = projectItem.unansweredClarificationsCount;

    if (count > 0 && props.onGoToClarifications) {
      return (
        <>
          <S_Link
            className='unanswered-clarifications'
            color='anchor'
            onClick={() => {
              void props.onGoToClarifications?.(projectItem.id);
            }}
          >
            {count}
          </S_Link>
        </>
      );
    } else {
      return <span>{count}</span>;
    }
  };

  const renderProgress = (projectItem: IProjectListItem): ReactElement => <>{projectItem.progress}%</>;

  const renderActions = (projectItem: IProjectListItem): ReactElement => {
    return (
      <>
        {projectItem.status === ProjectStatus.Draft && (
          <EnableFor hasPermission={projectsPermissions.canEdit()}>
            <PublishButtonContainer
              projectId={projectItem.id}
              disabled={!projectItem.canBePublished}
            >
              {(childrenProps): ReactElement => (
                <IconButton
                  size='sm'
                  color='primary'
                  onClick={() => {
                    void childrenProps.onClick();
                  }}
                  disabled={childrenProps.disabled}
                  title={t('publish')}
                >
                  <FontAwesomeIcon icon={'arrow-alt-circle-up'} />
                </IconButton>
              )}
            </PublishButtonContainer>
          </EnableFor>
        )}
        <EnableFor hasPermission={projectsPermissions.canEdit()}>
          <IconButton
            size='sm'
            onClick={(): void => {
              void props.onDelete(projectItem.id);
            }}
            title={t('delete')}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
        {projectItem.status === ProjectStatus.Published && (
          <>
            {projectItem.role === ProjectRole.Annotator && (
              <EnableFor hasPermission={annotationsPermissions.canAnnotate(projectItem.id)}>
                <Button
                  size='xs'
                  className='table-button'
                  onClick={(): void => {
                    props.onAnnotate(projectItem.id);
                  }}
                >
                  {t('annotate')}
                </Button>
              </EnableFor>
            )}
            {projectItem.role === ProjectRole.Reviewer && (
              <EnableFor hasPermission={annotationsPermissions.canReview(projectItem.id)}>
                <Button
                  size='xs'
                  className='table-button'
                  onClick={(): void => {
                    props.onReview(projectItem.id);
                  }}
                >
                  {t('review')}
                </Button>
              </EnableFor>
            )}
          </>
        )}
      </>
    );
  };

  const handleChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    context.setProjectsPageSize(pageSize);
    await props.pagination.onChange(pageNumber, pageSize);
  };

  const gColumns: IGColumnProps<IProjectListItem>[] = [
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: renderName,
    },
    {
      sortBy: 'modifiedDate',
      headerName: t('modified'),
      renderer: renderModifiedDate,
    },
    {
      sortBy: 'status',
      headerName: t('status'),
      renderer: renderStatus,
    },
    {
      sortBy: 'imagesCount',
      headerName: t('images'),
      renderer: (x) => x.imagesCount,
    },
    {
      sortBy: 'awaitingCount',
      headerName: t('awaiting'),
      renderer: renderAwaiting,
    },
    {
      sortBy: 'annotatedCount',
      headerName: t('annotated'),
      renderer: (x) => x.annotatedCount,
    },
    {
      sortBy: 'reviewedCount',
      headerName: t('reviewed'),
      renderer: (x) => x.reviewedCount,
    },
    {
      sortBy: 'unansweredClarificationsCount',
      headerName: t('clarifications.header'),
      renderer: renderUnansweredClarifications,
    },
    {
      sortBy: 'progress',
      headerName: t('progress'),
      renderer: renderProgress,
    },
    {
      sortBy: 'usersCount',
      headerName: t('workers'),
      renderer: (x) => x.usersCount,
    },
    {
      headerName: t('actions'),
      renderer: renderActions,
      actions: true,
      width: 'min-content',
    },
  ];

  return (
    <GTable<IProjectListItem>
      isLoading={props.isLoading}
      className='projects-list-table'
      columns={gColumns}
      items={props.projects}
      paginationProps={{ ...props.pagination, pageSize: context.store.projectsPageSize, onChange: handleChange }}
      onSortChanged={props.onOrderingChange}
      sortingModel={[props.sorting]}
      noItemsPlaceholder={t('no_projects_have_been_added_yet')}
    />
  );
});
